<template>
  <div class="search-content">
    <!-- Tabs -->
    <v-tabs
      v-model="activeTabLocal"
      color="#FEF3C7E6"
      align-tabs="center"
      class="search-tabs"
      density="compact"
      height="48"
    >
      <v-tab value="leagues" class="tab-title">
        <v-icon size="18" class="mr-2">mdi-trophy</v-icon>
        Ligas
      </v-tab>
      <v-tab value="teams" class="tab-title">
        <v-icon size="18" class="mr-2">mdi-shield</v-icon>
        Times
      </v-tab>
      <v-tab value="users" class="tab-title">
        <v-icon size="18" class="mr-2">mdi-account</v-icon>
        Usuários
      </v-tab>
    </v-tabs>

    <v-divider color="rgba(255, 255, 255, 0.1)"></v-divider>

    <!-- Content -->
    <div class="tab-content">
      <!-- Loading State -->
      <div v-if="loading" class="loading-state">
        <v-progress-circular
          indeterminate
          color="#FEF3C7E6"
          size="32"
        ></v-progress-circular>
        <p class="loading-text">Buscando...</p>
      </div>

      <!-- Initial Content - Show different content based on active tab -->
      <div v-else-if="!query" class="initial-content">
        
        <!-- LEAGUES TAB - Top Leagues -->
        <div v-if="activeTabLocal === 'leagues'">
          <!-- Header with "Ver Todas" button -->
          <div class="initial-header">
            <h3 class="initial-title">
              <v-icon class="mr-2" color="#FEF3C7E6">mdi-trophy</v-icon>
              Ligas Populares
            </h3>
            <!-- <v-btn
              variant="outlined"
              color="#FEF3C7E6"
              size="small"
              class="see-all-btn"
              @click="goToAllLeagues"
              rounded="lg"
            >
              Ver todas
              <v-icon size="16" class="ml-1">mdi-arrow-right</v-icon>
            </v-btn> -->
          </div>

          <!-- Top Leagues List -->
          <div v-if="loadingTopLeagues" class="loading-leagues">
            <v-progress-circular
              indeterminate
              color="#FEF3C7E6"
              size="24"
            ></v-progress-circular>
            <p class="loading-text-small">Carregando ligas...</p>
          </div>
          
          <v-list v-else-if="topLeagues.length > 0" class="top-leagues-list">
            <router-link
              v-for="(league, index) in topLeagues"
              :key="`${league.league_id}-${league.season}`"
              :to="{ name: 'LeaguePage', params: { name: slugify(league.league_name), id: league.league_id } }"
              class="league-stats-item-link"
              @click="emit('close-search')"
            >
              <v-list-item class="league-stats-item">
                <template v-slot:prepend>
                  <div class="league-rank">
                    <span class="rank-number">{{ index + 1 }}</span>
                  </div>
                  <v-avatar size="35" tile :border="0" class="league-avatar mx-1">
                    <img 
                      :src="`https://media.api-sports.io/football/leagues/${league.league_id}.png`"
                      :alt="league.league_name"
                      class="avatar-img"
                    />
                  </v-avatar>
                </template>

                <div class="league-info">
                  <v-list-item-title class="league-stats-title">
                    {{ league.league_name }}
                  </v-list-item-title>
                  <v-list-item-subtitle class="league-stats-subtitle">
                    {{ translateCountry(league.country) }} • {{ league.season }}
                  </v-list-item-subtitle>
                </div>

                <template v-slot:append>
                  <div class="league-statistics">
                    <div class="stat-item">
                      <span
                        class="mx-2 stat-value"
                        :style="{ color: ratingColor(league.avg_rating) }"
                      >
                        {{ league.avg_rating.toFixed(1) }}
                      </span>
                    </div>
                  </div>
                </template>
              </v-list-item>
            </router-link>
          </v-list>

          <!-- Empty state for top leagues -->
          <div v-else class="empty-state">
            <v-icon size="48" color="grey-darken-1" class="empty-icon">mdi-trophy-outline</v-icon>
            <h3 class="empty-title">Nenhuma estatística disponível</h3>
            <p class="empty-subtitle">As estatísticas das ligas aparecerão aqui em breve</p>
          </div>
        </div>

        <!-- TEAMS TAB - Empty State -->
        <div v-else-if="activeTabLocal === 'teams'" class="empty-query-state">
          <v-icon size="64" color="#FEF3C7E6" class="empty-icon">mdi-shield-search</v-icon>
          <h3 class="empty-title">Buscar Times</h3>
          <p class="empty-subtitle">Digite o nome do time ou país para encontrar clubes de futebol do mundo todo</p>
          <div class="search-examples">
            <span class="example-chip">Palmeiras</span>
            <span class="example-chip">Manchester</span>
            <span class="example-chip">Barcelona</span>
          </div>
        </div>

        <!-- USERS TAB - Empty State -->
        <div v-else-if="activeTabLocal === 'users'" class="empty-query-state">
          <v-icon size="64" color="#FEF3C7E6" class="empty-icon">mdi-account-search</v-icon>
          <h3 class="empty-title">Buscar Usuários</h3>
          <p class="empty-subtitle">Encontre outros usuários pelo nome ou @username para ver seus perfis e atividades</p>
          <div class="search-examples">
            <span class="example-chip">@joao</span>
            <span class="example-chip">Maria Silva</span>
            <span class="example-chip">@futebolfan</span>
          </div>
        </div>

        <!-- Quick tip -->
        <div class="search-tip">
          <v-icon size="16" color="grey-lighten-1" class="mr-2">mdi-lightbulb-outline</v-icon>
          <span class="tip-text">Digite acima para buscar {{ getTabLabel(activeTabLocal) }}</span>
        </div>
      </div>

      <!-- Results (when user has typed something) -->
      <div v-else-if="results.length > 0" class="results-container">
        <!-- Leagues Tab -->
        <div v-if="activeTabLocal === 'leagues'">
          <v-list class="search-list">
            <router-link
              v-for="league in results"
              :key="league.id"
              :to="{ name: 'LeaguePage', params: { name: slugify(league.name), id: league.id } }"
              class="search-list-item-link"
              @click="emit('close-search')"
            >
              <v-list-item class="search-list-item">
                <template v-slot:prepend>
                  <v-avatar size="48" class="league-avatar" tile :border="0">
                    <img 
                      :src="`https://media.api-sports.io/football/leagues/${league.id}.png`"
                      :alt="league.name"
                      class="avatar-img"
                    />
                  </v-avatar>
                </template>

                <v-list-item-title class="result-title">{{ league.name }}</v-list-item-title>
                <v-list-item-subtitle class="result-subtitle">
                  {{ translateCountry(league.country) }}
                </v-list-item-subtitle>
              </v-list-item>
            </router-link>
          </v-list>
        </div>

        <!-- Teams Tab -->
        <div v-else-if="activeTabLocal === 'teams'">
          <v-list class="search-list">
            <router-link
              v-for="team in results"
              :key="team.id"
              :to="{ name: 'TeamPage', params: { name: slugify(team.name), id: team.id } }"
              class="search-list-item-link"
              @click="emit('close-search')"
            >
              <v-list-item class="search-list-item">
                <template v-slot:prepend>
                  <v-avatar size="48" class="team-avatar" tile :border="0">
                    <img 
                      :src="`https://media.api-sports.io/football/teams/${team.id}.png`"
                      :alt="team.name"
                      class="avatar-img"
                    />
                  </v-avatar>
                </template>

                <v-list-item-title class="result-title">{{ team.name }}</v-list-item-title>
                <v-list-item-subtitle class="result-subtitle">{{ team.country }}</v-list-item-subtitle>
              </v-list-item>
            </router-link>
          </v-list>
        </div>

        <!-- Users Tab -->
        <div v-else-if="activeTabLocal === 'users'">
          <v-list class="search-list">
            <router-link
              v-for="user in results"
              :key="user.id"
              :to="{ name: 'ProfileBeta', params: { username: user.username } }"
              class="search-list-item-link"
              @click="emit('close-search')"
            >
              <v-list-item class="search-list-item">
                <template v-slot:prepend>
                  <v-avatar size="48" class="user-avatar" :border="0">
                    <img 
                      :src="user.profile_image_url || '/pp-placeholder.png'"
                      :alt="user.name"
                      class="avatar-img"
                    />
                  </v-avatar>
                </template>

                <v-list-item-title class="result-title">{{ user.name }}</v-list-item-title>
                <v-list-item-subtitle class="result-subtitle">@{{ user.username }}</v-list-item-subtitle>
              </v-list-item>
            </router-link>
          </v-list>
        </div>
      </div>

      <!-- No Results State -->
      <div v-else class="no-results-state">
        <v-icon size="48" color="grey-darken-1" class="empty-icon">mdi-magnify-remove</v-icon>
        <h3 class="empty-title">Nenhum resultado encontrado</h3>
        <p class="empty-subtitle">
          Não encontramos {{ getTabLabel(activeTabLocal) }} com o termo "{{ query }}"
        </p>
        <v-btn
          variant="outlined"
          color="#FEF3C7E6"
          size="small"
          class="mt-4"
          @click="switchToTab(getNextTab())"
        >
          Buscar em {{ getTabLabel(getNextTab()) }}
        </v-btn>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import countries from 'i18n-iso-countries'
import en from 'i18n-iso-countries/langs/en.json'
import pt from 'i18n-iso-countries/langs/pt.json'
import axios from '@/axios'
import { ratingColor } from '@/composables/ratingColors'
import { slugify } from '@/utils/slugify.js'

// Register locales for country translation
countries.registerLocale(en)
countries.registerLocale(pt)

// Router
const router = useRouter()

// Props
const props = defineProps({
  activeTab: {
    type: String,
    required: true
  },
  query: {
    type: String,
    default: ''
  },
  results: {
    type: Array,
    default: () => []
  },
  loading: {
    type: Boolean,
    default: false
  }
})

// Emits
const emit = defineEmits(['update:activeTab', 'result-click', 'team-click', 'league-click', 'close-search'])

// League statistics state
const topLeagues = ref([])
const loadingTopLeagues = ref(false)

// Computed
const activeTabLocal = computed({
  get: () => props.activeTab,
  set: (value) => emit('update:activeTab', value)
})

// Methods
function getTabIcon(tab) {
  const icons = {
    users: 'mdi-account-search',
    teams: 'mdi-shield-search',
    leagues: 'mdi-trophy-outline'
  }
  return icons[tab] || 'mdi-magnify'
}

function getEmptyTitle(tab) {
  const titles = {
    users: 'Buscar Usuários',
    teams: 'Buscar Times',
    leagues: 'Buscar Ligas'
  }
  return titles[tab] || 'Buscar'
}

function getEmptySubtitle(tab) {
  const subtitles = {
    users: 'Digite para encontrar usuários por nome ou @username',
    teams: 'Digite para encontrar times por nome ou país',
    leagues: 'Digite para encontrar ligas e competições'
  }
  return subtitles[tab] || 'Digite para buscar'
}

function getTabLabel(tab) {
  const labels = {
    users: 'usuários',
    teams: 'times',
    leagues: 'ligas'
  }
  return labels[tab] || 'resultados'
}

function getNextTab() {
  const tabs = ['leagues', 'teams', 'users'] // Reordenado para começar com leagues
  const currentIndex = tabs.indexOf(props.activeTab)
  return tabs[(currentIndex + 1) % tabs.length]
}

function switchToTab(tab) {
  activeTabLocal.value = tab
}

function goToAllLeagues() {
  // TODO: Implementar rota para página de todas as ligas
  console.log('Navegar para página de todas as ligas')
  // Quando criar a rota, descomente a linha abaixo:
  // router.push({ name: 'AllLeagues' })
  
  // Por enquanto, podemos fechar o search e mostrar um toast
  emit('close-search')
}

// Country translation helper
function translateCountry(countryName) {
  if (countryName === 'World') {
    return 'Internacional'
  }
  
  const countryCode = countries.getAlpha2Code(countryName, 'en')
  return countryCode 
    ? countries.getName(countryCode, 'pt')
    : countryName
}

// Fetch top leagues statistics
async function fetchTopLeagues() {
  loadingTopLeagues.value = true
  
  try {
    const response = await axios.get('/leagues/statistics', {
      params: {
        sortBy: 'records_count',
        sortOrder: 'desc',
        page: 1
      }
    })
    
    // Get top 5 leagues with most records
    topLeagues.value = response.data.data.slice(0, 5)
  } catch (error) {
    console.error('Erro ao buscar estatísticas das ligas:', error)
    topLeagues.value = []
  } finally {
    loadingTopLeagues.value = false
  }
}

// Lifecycle
onMounted(() => {
  fetchTopLeagues()
})
</script>

<style scoped>
.search-content {
  background-color: #14181c;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.search-tabs {
  background-color: rgba(30, 34, 39, 0.8);
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.tab-title {
  font-family: 'Poppins', sans-serif;
  font-weight: 500;
  font-size: 13px;
  text-transform: none;
}

/* Remove forçar cor branca - deixa o Vuetify gerenciar */

.tab-content {
  min-height: 300px;
  height: 100%;
  display: flex;
  flex-direction: column;
}

/* States */
.loading-state,
.no-results-state,
.empty-query-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px 24px;
  text-align: center;
  flex: 1;
}

/* Empty Query States for Teams/Users */
.search-examples {
  display: flex;
  gap: 8px;
  margin-top: 16px;
  flex-wrap: wrap;
  justify-content: center;
}

.example-chip {
  background-color: rgba(254, 243, 199, 0.1);
  color: white;
  padding: 4px 12px;
  border-radius: 16px;
  font-size: 12px;
  border: 1px solid rgba(254, 243, 199, 0.2);
}

/* Initial Content (Top Leagues) */
.initial-content {
  padding: 16px;
  flex: 1;
  display: flex;
  flex-direction: column;
}

.initial-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  padding: 0 8px;
}

.initial-title {
  color: var(--cream);
  font-size: 18px;
  font-weight: 600;
  font-family: 'Poppins', sans-serif;
  display: flex;
  align-items: center;
}

.see-all-btn {
  font-size: 12px;
  height: 32px;
  border-color: rgba(254, 243, 199, 0.4);
}

.see-all-btn:hover {
  background-color: rgba(254, 243, 199, 0.05);
  border-color: rgba(254, 243, 199, 0.6);
}

/* Loading Leagues */
.loading-leagues {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 32px 24px;
  text-align: center;
}

.loading-text-small {
  color: var(--cream);
  margin-top: 12px;
  font-size: 13px;
}

/* Top Leagues List */
.top-leagues-list {
  padding: 0;
  background-color: transparent;
  flex: 1;
}

.league-stats-item {
  padding: 10px 8px !important;
  margin-bottom: 8px;
  border-radius: 12px !important;
  border: 1px solid rgba(254, 243, 199, 0.1);
  transition: all 0.2s ease;
  cursor: pointer;
  background: var(--seaweed-green);
}

.league-stats-item:hover {
  background-color: rgba(254, 243, 199, 0.05);
  border-color: rgba(254, 243, 199, 0.2);
  transform: translateY(-1px);
}

.league-rank {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background-color: rgba(254, 243, 199, 0.15);
  margin-right: 6px;
}

.rank-number {
  color: #FEF3C7;
  font-weight: 700;
  font-size: 12px;
}

.league-info {
  flex: 1;
  min-width: 0;
}

.league-stats-title {
  color: white;
  font-size: 14px;
  font-weight: 600;
  font-family: 'Poppins', sans-serif;
  line-height: 1.2;
}

.league-stats-subtitle {
  color: rgba(222, 222, 222, 0.6);
  font-size: 12px;
  margin-top: 2px;
}

.league-statistics {
  display: flex;
  gap: 16px;
  align-items: center;
}

.stat-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}

.stat-label {
  color: rgba(254, 243, 199, 0.6);
  font-size: 10px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 2px;
}

.stat-value {
  color: var(--cream);
  font-size: 1.3rem;
  font-weight: 700;
}

/* Search Tip */
.search-tip {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 8px;
  padding: 12px 16px;
  background-color: rgba(30, 34, 39, 0.5);
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.05);
}

.tip-text {
  color: rgba(255, 255, 255, 0.6);
  font-size: 12px;
  font-style: italic;
}

.empty-icon {
  margin-bottom: 16px;
  opacity: 0.6;
}

.empty-title {
  color: white;
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 8px;
  font-family: 'Poppins', sans-serif;
}

.empty-subtitle {
  color: rgba(var(--v-theme-on-surface), 0.6);
  font-size: 14px;
  line-height: 1.4;
  max-width: 300px;
}

.loading-text {
  color: white;
  margin-top: 16px;
  font-size: 14px;
}

/* Results */
.results-container {
  flex: 1;
  overflow-y: auto;
}

.search-list {
  padding: 0;
  background-color: transparent;
}

.search-list-item {
  padding: 12px 16px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  transition: background-color 0.2s ease;
  cursor: pointer;
}

.search-list-item:hover {
  background-color: rgba(254, 243, 199, 0.05);
}

.search-list-item:last-child {
  border-bottom: none;
}

/* Router link styling - remove default styles */
.search-list-item-link,
.league-stats-item-link {
  text-decoration: none;
  color: inherit;
  display: block;
}

.search-list-item-link:hover,
.league-stats-item-link:hover {
  text-decoration: none;
  color: inherit;
}

/* Avatar Image Class */
.avatar-img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

/* Remove previous avatar styles */
.user-avatar,
.team-avatar,
.league-avatar {
  border: 2px solid rgba(254, 243, 199, 0.2);
}

.result-title {
  color: var(--cream);
  font-size: 16px;
  font-weight: 500;
  font-family: 'Poppins', sans-serif;
}

.result-subtitle {
  color: #FEF3C7E6;
  font-size: 14px;
  opacity: 0.8;
}

/* Scrollbar */
.results-container::-webkit-scrollbar {
  width: 4px;
}

.results-container::-webkit-scrollbar-track {
  background: rgba(255, 255, 255, 0.05);
}

.results-container::-webkit-scrollbar-thumb {
  background: rgba(254, 243, 199, 0.3);
  border-radius: 2px;
}

.results-container::-webkit-scrollbar-thumb:hover {
  background: rgba(254, 243, 199, 0.5);
}
</style>